import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { kycAPI } from '../services/api';
import { ArrowLeft, User, FileText, Check, X, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react';

interface Submission {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  kycStatus: string;
  kycDocuments?: Array<{
    type: string;
    url: string;
    uploadedAt: Date;
  }>;
  kycRejectionReason?: string;
  createdAt: string;
}

const AdminKycApprovalPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchSubmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      // Fetch all statuses (pending, approved, rejected)
      const response = await kycAPI.getAllKycSubmissions('all');
      if (response.success && response.data) {
        setSubmissions(response.data.submissions || []);
      }
    } catch (error: any) {
      console.error('Error fetching KYC submissions:', error);
      alert(error.response?.data?.message || 'Failed to load KYC submissions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchSubmissions();
  };

  const openDocument = (url: string) => {
    try {
      if (!url || !url.trim()) {
        alert('Document URL is not available');
        return;
      }

      let documentUrl = url.trim();
      if (!documentUrl.startsWith('http://') && !documentUrl.startsWith('https://')) {
        documentUrl = `https://${documentUrl}`;
      }

      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      console.error('Error opening document:', error);
      alert(`Failed to open document: ${error.message || 'Unknown error'}`);
    }
  };

  const handleReview = async (userId: string, status: 'approved' | 'rejected') => {
    if (status === 'rejected' && !rejectionReason[userId]?.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setReviewing(userId);
      const reason = status === 'rejected' ? rejectionReason[userId] : undefined;
      const response = await kycAPI.reviewKyc(userId, status, reason);
      
      if (response.success) {
        alert(`KYC ${status} successfully`);
        await fetchSubmissions();
        setRejectionReason({ ...rejectionReason, [userId]: '' });
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to review KYC');
    } finally {
      setReviewing(null);
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'id_proof':
        return 'ID Proof';
      case 'address_proof':
        return 'Address Proof';
      case 'driver_license':
        return 'Driver License';
      case 'passport_photo':
        return 'Passport Photo';
      default:
        return 'Other Document';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-gray-300">This page is only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-black animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading KYC submissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/admin')}
              className="mr-4 p-2 hover:bg-gray-800 rounded-lg"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">KYC Approvals</h1>
              <p className="text-gray-200 text-sm">Review and approve KYC documents</p>
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="ml-auto p-2 hover:bg-gray-800 rounded-lg disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {submissions.length === 0 ? (
          <div className="text-center py-12">
            <Check className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No KYC submissions</h2>
            <p className="text-gray-500">No KYC requests found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {submissions.map((submission) => {
              const getStatusBadge = (status: string) => {
                switch (status) {
                  case 'approved':
                    return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">Approved</span>;
                  case 'rejected':
                    return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">Rejected</span>;
                  case 'pending':
                    return <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">Pending</span>;
                  default:
                    return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">{status}</span>;
                }
              };

              return (
              <div key={submission._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start">
                    <div className="bg-gray-100 rounded-full p-3 mr-4">
                      <User className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{submission.name}</h3>
                      <p className="text-sm text-gray-600">{submission.email}</p>
                      <p className="text-sm text-gray-600">{submission.phone}</p>
                    </div>
                  </div>
                  {getStatusBadge(submission.kycStatus)}
                </div>

                {submission.kycRejectionReason && (
                  <div className="mb-6 flex items-start bg-red-50 border border-red-200 rounded-lg p-4">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-800">
                      <span className="font-semibold">Rejection reason:</span>{' '}
                      {submission.kycRejectionReason}
                    </p>
                  </div>
                )}

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Documents:</h4>
                  {submission.kycDocuments && submission.kycDocuments.length > 0 ? (
                    <div className="space-y-2">
                      {submission.kycDocuments.map((doc, index) => (
                        <button
                          key={index}
                          onClick={() => openDocument(doc.url)}
                          className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-lg p-3 transition-colors"
                        >
                          <div className="flex items-center">
                            <FileText className="h-5 w-5 text-black mr-3" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-gray-900">
                                {getDocumentTypeLabel(doc.type)}
                              </p>
                              <p className="text-xs text-gray-500 truncate max-w-md">{doc.url}</p>
                            </div>
                          </div>
                          <ExternalLink className="h-5 w-5 text-black" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No documents uploaded</p>
                  )}
                </div>

                {submission.kycStatus === 'pending' && (
                  <>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason (if rejecting):
                      </label>
                      <textarea
                        value={rejectionReason[submission._id] || ''}
                        onChange={(e) => setRejectionReason({ ...rejectionReason, [submission._id]: e.target.value })}
                        placeholder="Enter rejection reason..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-black"
                        rows={3}
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleReview(submission._id, 'rejected')}
                        disabled={reviewing === submission._id}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {reviewing === submission._id ? (
                          <RefreshCw className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <X className="h-5 w-5" />
                            <span>Reject</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleReview(submission._id, 'approved')}
                        disabled={reviewing === submission._id}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {reviewing === submission._id ? (
                          <RefreshCw className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-5 w-5" />
                            <span>Approve</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminKycApprovalPage;

